// The member's device is in a different time zone from the profile.
//
// A member with no availability (not a mentor): the profile follows the
// device at once, and a toast says so with an Undo. Only the display of
// times changes for them.
//
// A mentor: the sync also moves the availability. The clock times stay
// (9:00 to 17:00), so the real hours move. That needs a choice, so the
// sidebar shows a row (SidebarTimezoneRow) and nothing changes until the
// mentor says so.
//
// "Keep" and "Undo" are kept per device zone in this browser, so the same
// trip does not ask again.
//
// A member with no zone on the profile gets the device zone at once, with no
// toast and no question. A sign-up that does not go through the wizard has no
// zone, and booking is closed until the profile has one.
//
// Nothing here runs while a staff member is signed in to the admin on the
// same session (`sidebar.timezoneSync` is false). That is the "Become" path:
// the device is then the staff member's, not the member's.
import { computed, ref, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { DateTime } from 'luxon'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem'
import { ROLE } from '@/shared/data/permissionSystem'
import { showToast } from '@/shared/composables/useToast'

const SYNC_PATH = '/settings/profile/basic/sync_timezone'
// The device zone the member chose to keep the profile zone for.
const KEEP_KEY = 'gm-tz-keep'
// The device zone the automatic sync ran for in this tab, so a sync the
// server refused does not run on each page load.
const TRIED_KEY = 'gm-tz-tried'
// The toast to show after the reload that follows a sync.
const TOAST_KEY = 'gm-tz-toast'

// `asked`: the member clicked for the change, so a failure gets a message.
type AfterReload =
  | { kind: 'synced'; from: string; to: string; asked: boolean }
  | { kind: 'kept'; zone: string }

// "Europe/Athens" reads as "Athens", "America/New_York" as "New York".
export const zonePlace = (zone: string) =>
  (zone.split('/').pop() ?? zone).replace(/_/g, ' ')

// Two names for one clock are one zone here: "Asia/Kolkata" and
// "Asia/Calcutta", "Europe/Kyiv" and "Europe/Kiev", "EST" and
// "America/Panama". Browsers do not agree on the name, so a member with two
// devices would change zone at each visit, and no time would show
// differently. The check: the same offset from UTC in each week of the year
// to come.
const WEEKS = 53
export const isSameClock = (first: string, second: string) => {
  if (first === second) return true
  const start = DateTime.utc()
  for (let week = 0; week < WEEKS; week++) {
    const at = start.plus({ weeks: week })
    const here = at.setZone(first)
    const there = at.setZone(second)
    // A name Luxon does not know is not the same clock as a name it knows.
    if (!here.isValid || !there.isValid) return false
    if (here.offset !== there.offset) return false
  }
  return true
}

// null until mount: the server does not know the device zone.
const deviceZone = ref<string | null>(null)
const keptFor = ref<string | null>(null)
let isStarted = false

const read = (storage: 'localStorage' | 'sessionStorage', key: string) => {
  try {
    return window[storage].getItem(key)
  } catch {
    return null
  }
}
const write = (
  storage: 'localStorage' | 'sessionStorage',
  key: string,
  value: string | null,
) => {
  try {
    if (value === null) window[storage].removeItem(key)
    else window[storage].setItem(key, value)
  } catch {
    // No storage: the choice lasts until the page reloads.
  }
}

export const useTimezoneSync = () => {
  const page = usePage()
  const { currentUser, timezone } = useCurrentUser()
  const { hasRole, isUserLoggedIn } = usePermissionSystem()

  // `timezone` falls back to the device zone, so it cannot show that the
  // profile is empty.
  const hasNoZone = computed(
    () => isUserLoggedIn.value && !currentUser.value.timezone,
  )

  const isSyncOn = computed(
    () =>
      (page.props.shared as { sidebar?: { timezoneSync?: boolean } | null })
        .sidebar?.timezoneSync === true,
  )

  // The zone the profile would change to, or null when the two agree, the
  // member chose to keep the profile zone, or the sync is off.
  const targetZone = computed(() => {
    if (!isUserLoggedIn.value || !isSyncOn.value) return null
    if (!deviceZone.value) return null
    if (hasNoZone.value) return deviceZone.value
    if (isSameClock(deviceZone.value, timezone.value)) return null
    if (keptFor.value === deviceZone.value) return null
    return deviceZone.value
  })

  // A mentor has availability, so a mentor chooses. With no zone on the
  // profile there is no choice to make.
  const mode = computed<'auto' | 'ask'>(() =>
    hasRole(ROLE.MENTOR) && !hasNoZone.value ? 'ask' : 'auto',
  )

  // The row in the sidebar: only when the member has to choose.
  const ask = computed(() =>
    targetZone.value && mode.value === 'ask'
      ? { from: timezone.value, to: targetZone.value }
      : null,
  )

  const toastSynced = (from: string, to: string) => {
    showToast({
      text: `Times now show in ${zonePlace(to)} time.`,
      action: { label: 'Undo', run: () => undo(from) },
    })
  }
  const toastKept = (zone: string) => {
    showToast({ text: `Times stay in ${zonePlace(zone)} time.` })
  }

  // TIMEZONE SYNCHRONISATION: Reload the entire page to update all components
  // to use the new timezone. The toast shows after the reload.
  const syncOnServer = (zone: string, after: AfterReload | null) => {
    router.put(
      SYNC_PATH,
      { timezone: zone },
      {
        preserveScroll: true,
        onSuccess: () => {
          if (after) write('sessionStorage', TOAST_KEY, JSON.stringify(after))
          window.location.reload()
        },
      },
    )
  }

  const change = (asked = true) => {
    const from = timezone.value
    const to = targetZone.value
    if (!to) return
    syncOnServer(
      to,
      hasNoZone.value ? null : { kind: 'synced', from, to, asked },
    )
  }

  const keep = () => {
    keptFor.value = deviceZone.value
    write('localStorage', KEEP_KEY, deviceZone.value)
    toastKept(timezone.value)
  }

  // Back to the zone before the sync. The device zone has not changed, so
  // the choice is kept, or the next page load would sync again.
  const undo = (previousZone: string) => {
    if (timezone.value === previousZone) return toastKept(previousZone)
    write('localStorage', KEEP_KEY, deviceZone.value)
    syncOnServer(previousZone, { kind: 'kept', zone: previousZone })
  }

  // Once per page load, from the Layout, on the client.
  const init = () => {
    if (isStarted) return
    isStarted = true
    deviceZone.value = Intl.DateTimeFormat().resolvedOptions().timeZone
    keptFor.value = read('localStorage', KEEP_KEY)

    const pending = read('sessionStorage', TOAST_KEY)
    if (pending) {
      write('sessionStorage', TOAST_KEY, null)
      try {
        // The server answers a sync with a redirect also when it did not
        // save, so the profile zone on this page is the proof.
        const after = JSON.parse(pending) as AfterReload
        const zone = after.kind === 'synced' ? after.to : after.zone
        if (timezone.value !== zone) {
          if (after.kind === 'kept' || after.asked) {
            showToast({
              text: 'We could not change your time zone. Change it in Settings.',
              tone: 'error',
            })
          }
        } else if (after.kind === 'synced') {
          toastSynced(after.from, after.to)
        } else {
          toastKept(after.zone)
        }
      } catch {
        // A damaged value: no toast.
      }
    }

    // The automatic sync, for a member with no availability.
    watch(
      () => [targetZone.value, mode.value] as const,
      ([zone, currentMode]) => {
        if (!zone || currentMode !== 'auto') return
        if (read('sessionStorage', TRIED_KEY) === zone) return
        write('sessionStorage', TRIED_KEY, zone)
        change(false)
      },
      { immediate: true },
    )
  }

  return { ask, change, keep, init }
}
